/**
 * Critical Minerals layer renderer.
 * Renders mineral deposits as colored dots with mineral-type coloring
 * and production-status-based sizing.
 */

import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import { CRITICAL_MINERALS } from '@/config/data';
import type { CriticalMineralProject } from '@/config/data';

/** Layer IDs handled by this renderer. */
export const MINERALS_LAYER_IDS = new Set(['critical-minerals']);

// ---------------------------------------------------------------------------
// Color palette by mineral type
// ---------------------------------------------------------------------------

type RGBA = [number, number, number, number];

const MINERAL_COLORS: Record<string, RGBA> = {
  'lithium':      [59, 130, 246, 200],   // blue
  'cobalt':       [139, 92, 246, 200],   // purple
  'rare-earths':  [239, 68, 68, 200],    // red
  'rare earths':  [239, 68, 68, 200],    // red (alternate spelling)
  'nickel':       [34, 197, 94, 200],    // green
  'graphite':     [100, 116, 139, 200],  // slate
  'manganese':    [245, 158, 11, 200],   // amber
  'tungsten':     [234, 179, 8, 200],    // yellow
  'antimony':     [236, 72, 153, 200],   // pink
  'gallium':      [6, 182, 212, 200],    // cyan
  'germanium':    [20, 184, 166, 200],   // teal
  'pgm':          [212, 175, 55, 200],   // gold
  'copper':       [249, 115, 22, 200],   // orange
  'tantalum':     [168, 85, 247, 200],   // violet
  'niobium':      [99, 102, 241, 200],   // indigo
  'uranium':      [16, 185, 129, 200],   // emerald
  'tin':          [148, 163, 184, 200],  // gray
};

const DEFAULT_MINERAL_COLOR: RGBA = [148, 163, 184, 180];

// ---------------------------------------------------------------------------
// Sizing by production status
// ---------------------------------------------------------------------------

const STATUS_RADIUS: Record<string, number> = {
  'producing':    6,
  'active':       6,
  'development':  4,
  'exploration':  3,
  'suspended':    3,
};

const STATUS_STROKE: Record<string, RGBA> = {
  'producing':    [255, 255, 255, 120],
  'active':       [255, 255, 255, 120],
  'development':  [255, 255, 255, 80],
  'exploration':  [255, 255, 255, 50],
  'suspended':    [239, 68, 68, 100],
};

const DEFAULT_STROKE: RGBA = [255, 255, 255, 80];

// ---------------------------------------------------------------------------
// Layer factory
// ---------------------------------------------------------------------------

/** Create deck.gl layers for critical mineral deposits. */
export function createMineralsLayers(zoom: number): Layer[] {
  if (CRITICAL_MINERALS.length === 0) return [];

  const layers: Layer[] = [];
  const opacity = Math.min(1, 0.3 + zoom * 0.1);

  // Main dots
  layers.push(new ScatterplotLayer<CriticalMineralProject>({
    id: 'critical-minerals-dots',
    data: CRITICAL_MINERALS,
    pickable: true,
    filled: true,
    stroked: true,
    lineWidthUnits: 'pixels' as const,
    lineWidthMinPixels: 1,
    radiusUnits: 'pixels' as const,
    radiusMinPixels: 2,
    radiusMaxPixels: 10,
    getPosition: (d) => [d.lon, d.lat],
    getFillColor: (d) => MINERAL_COLORS[d.mineral.toLowerCase()] ?? DEFAULT_MINERAL_COLOR,
    getLineColor: (d) => STATUS_STROKE[d.status] ?? DEFAULT_STROKE,
    getLineWidth: 1,
    getRadius: (d) => STATUS_RADIUS[d.status] ?? 4,
    opacity,
    updateTriggers: {
      getFillColor: [CRITICAL_MINERALS.length],
      getLineColor: [CRITICAL_MINERALS.length],
      getRadius: [CRITICAL_MINERALS.length],
    },
  }));

  // Labels at higher zoom
  if (zoom >= 5) {
    layers.push(new TextLayer<CriticalMineralProject>({
      id: 'critical-minerals-labels',
      data: CRITICAL_MINERALS,
      getPosition: (d) => [d.lon, d.lat],
      getText: (d) => `${d.name} (${d.mineral})`,
      getSize: 10,
      getColor: [255, 255, 255, 200],
      getTextAnchor: 'start' as const,
      getAlignmentBaseline: 'center' as const,
      getPixelOffset: [10, 0],
      fontFamily: 'monospace',
      sizeUnits: 'pixels' as const,
      billboard: true,
      outlineWidth: 2,
      outlineColor: [0, 0, 0, 180],
    }));
  }

  return layers;
}
